import { Formik, useFormikContext  } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import * as Yup from "yup";
import "react-phone-input-2/lib/style.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import Select from 'react-select';
import { Progress } from "react-sweet-progress";
import { uploadFile } from "react-s3";
import { config } from "./../../utils/index";
import { toast } from "react-toastify";
import BackButton from "../../component/BackButton.jsx";
import Loader from "../../component/Loader.jsx";
import { CompanyDetailReducer, AddCompanyReducer } from "../slice";
import ukCitiesRaw from '../../map.json';
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:4060/api/";


const SUPPORTED_FORMATS = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const SUPPORTED_FORMATS_POLICY = ['application/pdf'];
const FILE_SIZE = 10 * 1024 * 1024; // 10MB

// Simple encryption/decryption for company ID
const encryptId = (id) => {
  const prefix = 'bic_enc_';
  const encoded = btoa(prefix + id);
  return encoded;
};

const decryptId = (encrypted) => {
  try {
    const decoded = atob(encrypted);
    const prefix = 'bic_enc_';
    if (decoded.startsWith(prefix)) {
      return decoded.substring(prefix.length);
    }
    return null;
  } catch (e) {
    return null;
  }
};

function EditCompany() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const details = useSelector((state) => state.main.companyDetail);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [savingSection, setSavingSection] = useState(null);
  const [openAccordion, setOpenAccordion] = useState('basic');

  const handleConnect = (e) => {
    e.preventDefault();
    const clientId = process.env.STRIPE_CONNECT_CLIENT_ID;
    const redirectUri = encodeURIComponent(process.env.BASE_URL+'company/stripe-connect');
    const state = 'bic_1234';

    const encryptedId = encryptId(id);
    const connectUrl = `https://connect.stripe.com/oauth/authorize?response_type=code&client_id=${clientId}&scope=read_write&redirect_uri=${redirectUri}&state=${encryptedId}`;

    window.location.href = connectUrl;
  };

  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? null : section);
  };


  const getDetails = useCallback(() => {
    setLoading(true);
    dispatch(
      CompanyDetailReducer({
        id: id,
      })
    ).then(() => {
      setLoading(false);
    });
  }, [dispatch, id]);
  const initialValues = {
    id: details?.id || "",
    company_name: details?.company_name || "",
    job_role: details?.job_role || "",
    trading_name: details?.trading_name || "",
    business_type: details?.business_type || "",
    pricing_structure: details?.pricing_structure || "",
    registration_number: details?.registration_number || "",
    vat_number: details?.vat_number || "",
    full_name: details?.full_name || "",
    address_line1: details?.address_line1 || "",
    address_line2: details?.address_line2 || "",
    city: details?.city || "",
    slug: details?.slug || "",
    county: details?.county || "",
    postcode: details?.postcode || "",
    country: details?.country || "",
    phone_number: details?.phone_number || "",
    email: details?.email || "",
    website: details?.website || "",
    logo: details?.logo || "",
    logo_preview: details?.logo || "",
    banner_preview: details?.banner || "",
    booking_cancellation_policy: details?.booking_cancellation_policy || "",
    services: details?.services || [],
    target_age:details?.target_age || "",
    no_of_weekly_bookings:details?.no_of_weekly_bookings || "",
    operating_hours:details?.operating_hours || "",
    brand_color:details?.brand_color || "",
    stripe_account_id:details?.stripe_account_id || "",
    notes:details?.notes || "",
    smtp_host:details?.smtp_host || "",
    smtp_username:details?.smtp_username || "",
    smtp_password:details?.smtp_password || "",
    smtp_from_email:details?.smtp_from_email || "",
  };
  
  useEffect(() => {
    if (id) {
      getDetails();
    }
  }, [getDetails, id]);

  // useEffect(() => {
  //   dispatch(FetchAreaCodeList({ page: 1, limit: 10000 }));
  // }, [dispatch]);

  useEffect(() => {
    let interval;
    if (isActive && time < 100) {
      interval = setInterval(() => {
        // if (time < 100)
        setTime((seconds) => (seconds < 99 ? seconds + 1 : seconds));
      }, 10);
    }
    return () => clearInterval(interval);
  }, [isActive, time]);
  
  const [countyCityMap, setCountyCityMap] = useState({});
  const [selectedCounty, setSelectedCounty] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const countries = ['UK'];

  // First load the county map
  useEffect(() => {
    const map = {};
    ukCitiesRaw.forEach(entry => {
      const city = entry[0];
      const county = entry[5] || "Unknown";
      if (!map[county]) {
        map[county] = [];
      }
      if (!map[county].includes(city)) {
        map[county].push(city);
      }
    });
    setCountyCityMap(map);
  }, []);

    // Then restore form data after map is loaded
    useEffect(() => {
      if (Object.keys(countyCityMap).length > 0) {
        const saved = localStorage.getItem("companyForm");
        if (saved) {
          const data = JSON.parse(saved);
          if (data.county) {
            setSelectedCounty(data.county);
          }
          if (data.city) {
            setSelectedCity(data.city);
          }
        }
      }
    }, [countyCityMap]);
  
    const handleCountyChange = (e) => {
      const newCounty = e.target.value;
      setSelectedCounty(newCounty);
      setSelectedCity('');
      setManualCity('');
      setShowManualInput(false);
      //setFieldValue('county', selectedCounty);
    };
  
    const handleCityChange = (e) => {
      if (e.target.value === 'manual') {
        setSelectedCity('');
        setShowManualInput(true);
      } else {
        setSelectedCity(e.target.value);
        setShowManualInput(false);
      }
    };
    setTimeout(() => {
      setSelectedCounty(details.county)
    }, 3000);

  // Section-wise save handlers
  const handleSaveBasicDetails = async (values, setTouched) => {
    // Set touched for all basic details fields to show validation errors
    await setTouched({
      company_name: true,
      trading_name: true,
      business_type: true,
      registration_number: true,
      vat_number: true,
      website: true,
      banner: true,
      logo: true,
    });

    // Validate required fields
    const basicSchema = Yup.object({
      company_name: Yup.string().required("Required"),
      trading_name: Yup.string().required("Required"),
      business_type: Yup.string().required("Required"),
      registration_number: Yup.string().when('business_type', {
        is: 'Ltd',
        then: Yup.string().required("Required for Ltd companies"),
        otherwise: Yup.string()
      }),
      vat_number: Yup.string(),
      website: Yup.string().required("Required"),
      banner: Yup.mixed().nullable().test(
        'banner-required',
        'Required',
        function(value) {
          // If there's an existing banner preview, no new file is required
          if (this.parent.banner_preview) return true;
          // Otherwise, a file is required
          return !!value;
        }
      ),
      logo: Yup.mixed().nullable().test(
        'logo-required',
        'Required',
        function(value) {
          // If there's an existing logo preview, no new file is required
          if (this.parent.logo_preview) return true;
          // Otherwise, a file is required
          return !!value;
        }
      ),
    });

    try {
      await basicSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingSection('basic');
    try {
      const payload = {
        id: values?.id,
        company_name: values?.company_name,
        trading_name: values?.trading_name,
        business_type: values?.business_type,
        registration_number: values?.registration_number,
        vat_number: values?.vat_number,
        website: values?.website,
        slug: values?.slug,
        stripe_account_id: values?.stripe_account_id,
        logo: selectedLogo,
        banner: selectedBanner,
      };

      const res = await dispatch(AddCompanyReducer(payload));
      if (res?.payload?.code === 1) {
        toast.success('Basic details saved successfully');
        if (res?.payload?.data) {
          if (!handleProfileCompletionRedirect(res?.payload)) {
            dispatch(CompanyDetailReducer({ id: id }));
          }
        }
      } else {
        toast.error(res?.payload?.message || 'Failed to save basic details');
      }
    } catch (error) {
      toast.error('Failed to save basic details');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveContactInfo = async (values, setTouched) => {
    // Set touched for all contact info fields to show validation errors
    await setTouched({
      full_name: true,
      job_role: true,
      email: true,
      phone_number: true,
    });

    // Validate required fields
    const contactSchema = Yup.object({
      full_name: Yup.string().required("Required"),
      job_role: Yup.string().required("Required"),
      email: Yup.string().email("Invalid email format").required("Required"),
      phone_number: Yup.string().required("Required"),
    });

    try {
      await contactSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingSection('contact');
    try {
      const payload = {
        id: values?.id,
        full_name: values?.full_name,
        job_role: values?.job_role,
        email: values?.email,
        phone_number: values?.phone_number,
      };

      const res = await dispatch(AddCompanyReducer(payload));
      if (res?.payload?.code === 1) {
        toast.success('Contact information saved successfully');
        if (res?.payload?.data) {
          if (!handleProfileCompletionRedirect(res?.payload)) {
            dispatch(CompanyDetailReducer({ id: id }));
          }
        }
      } else {
        toast.error(res?.payload?.message || 'Failed to save contact information');
      }
    } catch (error) {
      toast.error('Failed to save contact information');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveBusinessAddress = async (values, setTouched) => {
    // Set touched for all address fields to show validation errors
    await setTouched({
      address_line1: true,
      address_line2: true,
      county: true,
      city: true,
      country: true,
      postcode: true,
    });

    // Validate required fields
    const addressSchema = Yup.object({
      address_line1: Yup.string().required("Required"),
      address_line2: Yup.string(),
      county: Yup.string().required("Required"),
      city: Yup.string().required("Required"),
      country: Yup.string().required("Required"),
      postcode: Yup.string().required("Required"),
    });

    try {
      await addressSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingSection('address');
    try {
      const payload = {
        id: values?.id,
        address_line1: values?.address_line1,
        address_line2: values?.address_line2,
        city: values?.city === "manual" ? manualCity : values?.city,
        county: values?.county,
        postcode: values?.postcode,
        country: values?.country,
      };

      const res = await dispatch(AddCompanyReducer(payload));
      if (res?.payload?.code === 1) {
        toast.success('Business address saved successfully');
        if (res?.payload?.data) {
          if (!handleProfileCompletionRedirect(res?.payload)) {
            dispatch(CompanyDetailReducer({ id: id }));
          }
        }
      } else {
        toast.error(res?.payload?.message || 'Failed to save business address');
      }
    } catch (error) {
      toast.error('Failed to save business address');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveBusinessOperation = async (values, setTouched) => {
    // Set touched for all business operation fields to show validation errors
    await setTouched({
      services: true,
      target_age: true,
      notes: true,
    });

    // Validate required fields
    const operationSchema = Yup.object({
      services: Yup.array().min(1, "At least one service must be selected"),
      target_age: Yup.string().required("Required"),
      notes: Yup.string(),
    });

    try {
      await operationSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingSection('operation');
    try {
      const payload = {
        id: values?.id,
        services: values?.services,
        target_age: values?.target_age,
        notes: values?.notes,
      };

      payload.services = JSON.stringify(values.services);

      const res = await dispatch(AddCompanyReducer(payload));
      if (res?.payload?.code === 1) {
        toast.success('Business operation saved successfully');
        if (res?.payload?.data) {
          if (!handleProfileCompletionRedirect(res?.payload)) {
            dispatch(CompanyDetailReducer({ id: id }));
          }
        }
      } else {
        toast.error(res?.payload?.message || 'Failed to save business operation');
      }
    } catch (error) {
      toast.error('Failed to save business operation');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSaveSMTPConfig = async (values, setTouched) => {
    // Set touched for all SMTP fields to show validation errors
    await setTouched({
      smtp_host: true,
      smtp_username: true,
      smtp_password: true,
      smtp_from_email: true,
    });

    // Validate required fields
    const smtpSchema = Yup.object({
      smtp_host: Yup.string().required("Required"),
      smtp_username: Yup.string().required("Required"),
      smtp_password: Yup.string().required("Required"),
      smtp_from_email: Yup.string().email("Invalid email format").required("Required"),
    });

    try {
      await smtpSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required fields');
      return;
    }

    setSavingSection('smtp');
    try {
      const payload = {
        id: values?.id,
        smtp_host: values?.smtp_host,
        smtp_username: values?.smtp_username,
        smtp_password: values?.smtp_password,
        smtp_from_email: values?.smtp_from_email,
      };

      const res = await dispatch(AddCompanyReducer(payload));
      if (res?.payload?.code === 1) {
        toast.success('SMTP configuration saved successfully');
        if (res?.payload?.data) {
          if (!handleProfileCompletionRedirect(res?.payload)) {
            dispatch(CompanyDetailReducer({ id: id }));
          }
        }
      } else {
        toast.error(res?.payload?.message || 'Failed to save SMTP configuration');
      }
    } catch (error) {
      toast.error('Failed to save SMTP configuration');
    } finally {
      setSavingSection(null);
    }
  };

  const handleSendTestEmail = async (values) => {
    // Validate required fields before sending test email
    const smtpSchema = Yup.object({
      smtp_host: Yup.string().required("Required"),
      smtp_username: Yup.string().required("Required"),
      smtp_password: Yup.string().required("Required"),
      smtp_from_email: Yup.string().email("Invalid email format").required("Required"),
    });

    try {
      await smtpSchema.validate(values, { abortEarly: false });
    } catch (validationError) {
      toast.error('Please fill all required SMTP fields before sending test email');
      return;
    }

    setSavingSection('test-email');
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${BASE_URL}admin/sendTestEmail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "token": token
          },
          body: JSON.stringify({
            smtp_host: values.smtp_host,
            smtp_username: values.smtp_username,
            smtp_password: values.smtp_password,
            smtp_from_email: values.smtp_from_email,
            company_id: values.id
          })
        }
      );

      const data = await response.json();
      if (data.code === 1) {
        toast.success('Test email sent successfully! Please check your inbox.');
      } else {
        toast.error(data.message || 'Failed to send test email');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setSavingSection(null);
    }
  };

  const handleProfileCompletionRedirect = (payload) => {
    const isProfileComplete = payload?.data?.is_profile_complete == 1;
    const isBasicProfileComplete = payload?.data?.is_basic_profile_complete == 1;
    if (!isProfileComplete && isBasicProfileComplete) {
      localStorage.setItem('showOnboardingAfterProfileComplete', 'true');
       navigate("/teacher", { replace: true });
      return true;
    }
    return false;
  };

  if (loading) {
    return (
      <article className="col-lg-9 articleClass">
        {" "}
        <Loader />
      </article>
    );
  }
  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center col-rever">
          <BackButton />
          <div className="col-sm-12 col-md-12 col-lg-12 mb-3 text-center">
            <h3>{details?.is_profile_complete ? "Edit Company" : "Complete Profile"}</h3>
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-sm-12">
            <Formik
              initialValues={initialValues}
              validationSchema={Yup.object({
                company_name: Yup.string().required("Required"),
                trading_name: Yup.string().required("Required"),
                business_type: Yup.string().required("Required"),
                registration_number: Yup.string().when('business_type', {
                  is: 'Ltd',
                  then: Yup.string().required("Required for Ltd companies"),
                  otherwise: Yup.string()
                }),
                vat_number: Yup.string(),
                website: Yup.string().required("Required"),
                banner: Yup
                    .mixed()
                    .nullable()
                    .test("bannerRequired", "Required", function(value) {
                      // Pass if new file selected OR existing preview exists
                      if (value && value[0]) return true;
                      if (this.parent.banner_preview) return true;
                      return false;
                    })
                    .test("fileSize", "Banner must be less than 10MB", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return value[0]?.size <= FILE_SIZE;
                    })
                    .test("fileFormat", "Unsupported file format", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return SUPPORTED_FORMATS.includes(value[0]?.type);
                    }),
                logo: Yup
                    .mixed()
                    .nullable()
                    .test("logoRequired", "Required", function(value) {
                      // Pass if new file selected OR existing preview exists
                      if (value && value[0]) return true;
                      if (this.parent.logo_preview) return true;
                      return false;
                    })
                    .test("fileSize", "Logo must be less than 10MB", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return value[0]?.size <= FILE_SIZE;
                    })
                    .test("fileFormat", "Unsupported file format", (value) => {
                      if (!value || !value[0] || !(value instanceof FileList)) return true; // Skip if no file selected
                      return SUPPORTED_FORMATS.includes(value[0]?.type);
                    }),
                full_name: Yup.string().required("Required"),
                job_role: Yup.string().required("Required"),
                email: Yup.string()
                  .email("Invalid email format")
                  .required("Required")
                  .test("checkEmailDuplication", "This email is already in use", async function(value) {
                    if (!value) return true;
                    try {
                      const token = localStorage.getItem("token");
                      const response = await fetch(
                        `${BASE_URL}admin/checkEmailDuplication?email=${encodeURIComponent(value)}&id=${id}&user_type=company`,
                        { headers: { token } }
                      );
                      const data = await response.json();
                      return !data.data.exists;
                    } catch (err) {
                      return true;
                    }
                  }),
                phone_number: Yup.string().required("Required"),
                address_line1: Yup.string().required("Required"),
                address_line2: Yup.string(),
                county: Yup.string().required("Required"),
                city: Yup.string().required("Required"),
                country: Yup.string().required("Required"),
                postcode: Yup.string().required("Required"),
                services: Yup.array().min(1, "At least one service must be selected"),
                target_age: Yup.string().required("Required"),
                notes: Yup.string(),
                smtp_host: Yup.string().required("Required"),
                smtp_username: Yup.string().required("Required"),
                smtp_password: Yup.string().required("Required"),
                smtp_from_email: Yup.string().required("Required"),
              })}
              enableReinitialize
              onSubmit={async (values) => {
                setSubmitting(true);

                let payload = {
                  id: values?.id,
                  company_name: values?.company_name,
                  trading_name: values?.trading_name,
                  full_name: values?.full_name,
                  job_role: values?.job_role,
                  business_type: values?.business_type,
                  registration_number: values?.registration_number,
                  vat_number: values?.vat_number,
                  address_line1: values?.address_line1,
                  address_line2: values?.address_line2,
                  city: values?.city,
                  slug: values?.slug,
                  county: values?.county,
                  postcode: values?.postcode,
                  country: values?.country,
                  phone_number: values?.phone_number,
                  email: values?.email,
                  website: values?.website,
                  logo: selectedLogo,
                  banner: selectedBanner,
                  notes: values?.notes,
                  services: values?.services,
                  target_age: values?.target_age,
                  stripe_account_id: values?.stripe_account_id,
                  smtp_host: values?.smtp_host,
                  smtp_username: values?.smtp_username,
                  smtp_password: values?.smtp_password,
                  smtp_from_email: values?.smtp_from_email,
                };
                
                if (payload.city === "manual") {
                  payload.city = manualCity;
                }
          
                payload.services = JSON.stringify(values.services);
                  
                dispatch(
                  AddCompanyReducer(payload)
                ).then((res) => {
                  setSubmitting(false);

                  if (res?.payload?.code === 1) {
                    toast.success(res?.payload?.message);
                    const isProfileComplete = res?.payload?.data?.is_profile_complete == 1;
                    const isBasicProfileComplete = res?.payload?.data?.is_basic_profile_complete == 1;
                    if (!isProfileComplete && isBasicProfileComplete) {
                      localStorage.setItem('showOnboardingAfterProfileComplete', 'true');
                      navigate("/area-code", { replace: true });
                    } else {
                      navigate("/dashboard", { replace: true });
                    }
                  } else {
                    toast?.error(res?.payload?.message);
                  }
                });
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFormikState,
                setFieldValue,
                setTouched,
                /* and other goodies */
              }) => (
                <form className="form-style" onSubmit={handleSubmit}>
                  <input type="hidden" name="id" value={values.id} />
                  <div className="row" style={{ margin: 0 }}>
                    {/* Basic Details Accordion */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header d-flex justify-content-between align-items-center p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('basic')}
                        >
                          <h5 style={{ margin: 0, fontSize: '16px' }}>Basic Details</h5>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.2s ease',
                            transform: openAccordion === 'basic' ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>▼</span>
                        </div>
                        {openAccordion === 'basic' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group text-center">
                                  <label className="fw-bold">Banner</label>
                                  <div className="upload-btn-wrapper" style={{ borderRadius: '0px' }}>
                                    <button className="btn">
                                      <img
                                        src={
                                          values.banner_preview
                                            ? values.banner_preview
                                            : "assets/images/upload-img.png"
                                        }
                                        alt=""
                                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                                      />
                                    </button>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      name="banner"
                                      onChange={(e) => {
                                        if (e.target.files.length > 0) {
                                          const file = e.target.files[0];
                                          setSelectedBanner(file);
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            setFieldValue('banner_preview', reader.result);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                                {errors.banner && touched.banner && (
                                  <div className="text-red">{errors.banner}</div>
                                )}
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group text-center">
                                  <label className="fw-bold">Logo</label>
                                  <div className="upload-btn-wrapper">
                                    <button className="btn">
                                      <img
                                        src={
                                          values.logo_preview
                                            ? values.logo_preview
                                            : "assets/images/upload-img.png"
                                        }
                                        alt=""
                                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                                      />
                                    </button>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      name="logo"
                                      onChange={(e) => {
                                        if (e.target.files.length > 0) {
                                          const file = e.target.files[0];
                                          setSelectedLogo(file);
                                          const reader = new FileReader();
                                          reader.onloadend = () => {
                                            setFieldValue('logo_preview', reader.result);
                                          };
                                          reader.readAsDataURL(file);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                                {errors.logo && touched.logo && (
                                  <div className="text-red">{errors.logo}</div>
                                )}
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <label className="fw-bold">Company Name</label>
                                {errors.company_name && touched.company_name && (
                                  <span className="text-red ms-1">{errors.company_name}</span>
                                )}
                                <div className="form-group">
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="company_name"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.company_name}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Trading Name</label>
                                  {errors.trading_name && touched.trading_name && (
                                    <span className="text-red ms-1">
                                      {errors.trading_name}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="trading_name"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.trading_name}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/address.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">VAT Number</label>
                                  {errors.vat_number && touched.vat_number && (
                                    <span className="text-red ms-1">
                                      {errors.vat_number}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="vat_number"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.vat_number}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Business Type</label>
                                  {errors.business_type && touched.business_type && (
                                    <span className="text-red ms-1">
                                      {errors.business_type}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <select className="form-control pl-5" name="business_type" onChange={handleChange} onBlur={handleBlur} value={values.business_type} style={{ backgroundColor: '#f5f5f5' }}>
                                      <option value="">Select</option>
                                      <option value="Ltd">Ltd</option>
                                      <option value="Sole Trader">Sole Trader</option>
                                      <option value="Charity">Charity</option>
                                    </select>
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Registration Number</label>
                                  {errors.registration_number && touched.registration_number && (
                                    <span className="text-red ms-1">
                                      {errors.registration_number}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="registration_number"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.registration_number}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Website</label>
                                  {errors.website && touched.website && (
                                    <span className="text-red ms-1">
                                      {errors.website}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="website"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.website}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-3 col-md-3 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Slug</label>
                                  {errors.slug && touched.slug && (
                                    <span className="text-red ms-1">
                                      {errors.slug}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="slug"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.slug}
                                      readOnly
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-3 col-md-3" style={{ lineHeight: '7' }}> <span>.bookinclass.co.uk</span></div>


                              <div className="col-sm-6 col-md-6 mb-4 d-none">
                                <div className="form-group">
                                  <label className="fw-bold">Stripe Account ID</label>
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="stripe_account_id"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.stripe_account_id}
                                      readOnly
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSaveBasicDetails(values, setTouched)}
                                disabled={savingSection === 'basic'}
                              >
                                {savingSection === 'basic' ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Primary Contact Information Accordion */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header d-flex justify-content-between align-items-center p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('contact')}
                        >
                          <h5 style={{ margin: 0, fontSize: '16px' }}>Primary Contact Information</h5>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.2s ease',
                            transform: openAccordion === 'contact' ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>▼</span>
                        </div>
                        {openAccordion === 'contact' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Full Name</label>
                                  {errors.full_name && touched.full_name && (
                                    <span className="text-red ms-1">
                                      {errors.full_name}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="full_name"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.full_name}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Job Title</label>
                                  {errors.job_role && touched.job_role && (
                                    <span className="text-red ms-1">
                                      {errors.job_role}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="job_role"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.job_role}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Email Address</label>
                                  {errors.email && touched.email && (
                                    <span className="text-red ms-1">
                                      {errors.email}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="email"
                                      className="form-control pl-5"
                                      name="email"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.email}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Phone Number</label>
                                  {errors.phone_number && touched.phone_number && (
                                    <span className="text-red ms-1">
                                      {errors.phone_number}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="phone_number"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.phone_number}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSaveContactInfo(values, setTouched)}
                                disabled={savingSection === 'contact'}
                              >
                                {savingSection === 'contact' ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>


                    {/* Business Address Accordion */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header d-flex justify-content-between align-items-center p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('address')}
                        >
                          <h5 style={{ margin: 0, fontSize: '16px' }}>Business Address</h5>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.2s ease',
                            transform: openAccordion === 'address' ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>▼</span>
                        </div>
                        {openAccordion === 'address' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Address Line 1</label>
                                  {errors.address_line1 && touched.address_line1 && (
                                    <span className="text-red ms-1">
                                      {errors.address_line1}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="address_line1"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.address_line1}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Address Line 2</label>
                                  {errors.address_line2 && touched.address_line2 && (
                                    <span className="text-red ms-1">
                                      {errors.address_line2}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="address_line2"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.address_line2}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">County</label>
                                  <select
                                    className="form-control pl-5"
                                    value={values.county}
                                    onChange={(e) => {
                                      const selectedCounty = e.target.value;
                                      setFieldValue('county', selectedCounty);
                                      handleCountyChange(e);
                                    }}
                                    style={{ backgroundColor: '#f5f5f5' }}
                                  >
                                    <option value="">Select County</option>
                                    {Object.keys(countyCityMap).sort().map(county => (
                                      <option key={county} value={county}>{county}</option>
                                    ))}
                                  </select>
                                  {errors.county && touched.county && (
                                    <span className="text-red ms-1">
                                      {errors.county}
                                    </span>
                                  )}
                                  <div className="input-container "></div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">City</label>
                                  <select
                                    className="form-control pl-5"
                                    value={values.city || (showManualInput ? "manual" : "")}
                                    onChange={(e) => {
                                      const selectedCity = e.target.value;
                                      setFieldValue('city', selectedCity);
                                      handleCityChange(e);
                                    }}
                                    style={{ backgroundColor: '#f5f5f5' }}
                                  >
                                    <option value="">Select City/Town</option>
                                    {selectedCounty && countyCityMap[selectedCounty]?.sort().map(city => (
                                      <option key={city} value={city}>{city}</option>
                                    ))}
                                    {selectedCounty && values.city && 
                                      !countyCityMap[selectedCounty]?.includes(values.city) && 
                                      values.city !== 'manual' && (
                                      <option key={values.city} value={values.city}>{values.city}</option>
                                    )}
                                    <option value="manual">My town/village isn't listed</option>
                                  </select>
                                  {showManualInput && (
                                    <div className="mt-4">
                                      <label>Enter City/Town:</label>
                                      <input
                                        type="text"
                                        placeholder="Enter your town or village"
                                        value={manualCity}
                                        onChange={(e) => setManualCity(e.target.value)}
                                        style={{ backgroundColor: '#f5f5f5' }}
                                      />
                                    </div>
                                  )}
                                  {errors.city && touched.city && (
                                    <span className="text-red ms-1">
                                      {errors.city}
                                    </span>
                                  )}
                                  <div className="input-container"></div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Country</label>
                                  <select className="form-control pl-5" value={values.country} style={{ backgroundColor: '#f5f5f5' }}>
                                    <option value="">Select Country</option>
                                    {countries.map((country) => (
                                      <option key={country} value={country}>{country}</option>
                                    ))}
                                  </select>
                                  {errors.country && touched.country && (
                                    <span className="text-red ms-1">
                                      {errors.country}
                                    </span>
                                  )}
                                  <div className="input-container"></div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Postcode</label>
                                  {errors.postcode && touched.postcode && (
                                    <span className="text-red ms-1">
                                      {errors.postcode}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="postcode"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.postcode}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSaveBusinessAddress(values, setTouched)}
                                disabled={savingSection === 'address'}
                              >
                                {savingSection === 'address' ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Business Operation Accordion */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header d-flex justify-content-between align-items-center p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('operation')}
                        >
                          <h5 style={{ margin: 0, fontSize: '16px' }}>Business Operation</h5>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.2s ease',
                            transform: openAccordion === 'operation' ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>▼</span>
                        </div>
                        {openAccordion === 'operation' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Services Offered</label>
                                  {errors.services && touched.services && (
                                    <span className="text-red ms-1">
                                      {errors.services}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <Select
                                      isMulti
                                      options={[
                                        { value: "Martial Arts", label: "Martial Arts" },
                                        { value: "Holiday Clubs", label: "Holiday Clubs" },
                                        { value: "Tuition", label: "Tuition" },
                                        { value: "Sports Coaching", label: "Sports Coaching" },
                                        { value: "After School Clubs", label: "After School Clubs" },
                                        { value: "Other", label: "Other" }
                                      ]}
                                      value={
                                        values.services.map(service => ({
                                          value: service,
                                          label: service
                                        }))
                                      }
                                      className="select2-container form-control"
                                      classNamePrefix="select2"
                                      onChange={(selectedOptions) => {
                                        setFieldValue("services", selectedOptions.map(option => option.value), { shouldValidate: true });
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Target Age Range</label>
                                  {errors.target_age && touched.target_age && (
                                    <span className="text-red ms-1">
                                      {errors.target_age}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="target_age"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.target_age}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Hidden fields - Estimated Weekly Bookings and Typical Operating Hours */}
                              {/* <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Estimated Weekly Bookings</label>
                                  {errors.no_of_weekly_bookings && touched.no_of_weekly_bookings && (
                                    <span className="text-red ms-1">
                                      {errors.no_of_weekly_bookings}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="no_of_weekly_bookings"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.no_of_weekly_bookings}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Typical Operating Hours</label>
                                  {errors.operating_hours && touched.operating_hours && (
                                    <span className="text-red ms-1">
                                      {errors.operating_hours}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="operating_hours"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.operating_hours}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div> */}

                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Custom Features or Notes</label>
                                  {errors.notes && touched.notes && (
                                    <span className="text-red ms-1">
                                      {errors.notes}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <textarea
                                      type="text"
                                      className="form-control"
                                      name="notes"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.notes}
                                      rows="20"
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                  </div>
                                </div>
                              </div>

                              {/* Hidden fields - Pricing Structure and Brand Colour */}
                              {/* <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Pricing Structure</label>
                                  {errors.pricing_structure && touched.pricing_structure && (
                                    <span className="text-red ms-1">
                                      {errors.pricing_structure}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <select
                                      name="pricing_structure"
                                      className="form-control pl-5"
                                      value={values.pricing_structure}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    >
                                      <option value="">-- Select a Pricing Structure --</option>
                                      <option value="Flate Rate">Flate Rate</option>
                                      <option value="Per Session">Per Session</option>
                                      <option value="Tiered">Tiered</option>
                                    </select>
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>

                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Brand Colour(HEX Code)</label>
                                  {errors.brand_color && touched.brand_color && (
                                    <span className="text-red ms-1">
                                      {errors.brand_color}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="brand_color"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.brand_color}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div> */}
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSaveBusinessOperation(values, setTouched)}
                                disabled={savingSection === 'operation'}
                              >
                                {savingSection === 'operation' ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>


                    {/* SMTP Configuration Accordion */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('smtp')}
                        >
                          <div className="d-flex justify-content-between align-items-center">
                            <h5 style={{ margin: 0, fontSize: '16px' }}>Email Settings</h5>
                            <span style={{
                              fontSize: '12px',
                              color: '#666',
                              transition: 'transform 0.2s ease',
                              transform: openAccordion === 'smtp' ? 'rotate(180deg)' : 'rotate(0deg)',
                              display: 'inline-block'
                            }}>▼</span>
                          </div>
                         
                        </div>
                        {openAccordion === 'smtp' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-12 col-md-12">
                               <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#666' }}>
                                Enter the email account details you would like BookinClass to use when sending emails to your customers. If you're unsure what to enter, please contact your email provider or web hosting company.
                              </p>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Outgoing Email Server</label>
                                  {errors.smtp_host && touched.smtp_host && (
                                    <span className="text-red ms-1">
                                      {errors.smtp_host}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="smtp_host"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.smtp_host}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                      placeHolder="mail.yourdomain.co.uk"
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Email Address</label>
                                  {errors.smtp_username && touched.smtp_username && (
                                    <span className="text-red ms-1">
                                      {errors.smtp_username}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="smtp_username"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.smtp_username}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                       placeHolder="info@yourclub.co.uk"
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Email Password</label>
                                  {errors.smtp_password && touched.smtp_password && (
                                    <span className="text-red ms-1">
                                      {errors.smtp_password}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type={showPassword ? "text" : "password"}
                                      className="form-control pl-5"
                                      name="smtp_password"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.smtp_password}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                      placeHolder="Your email account password"
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-sm-6 col-md-6 mt-4 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Sender Email Address</label>
                                  {errors.smtp_from_email && touched.smtp_from_email && (
                                    <span className="text-red ms-1">
                                      {errors.smtp_from_email}
                                    </span>
                                  )}
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="smtp_from_email"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.smtp_from_email}
                                      style={{ backgroundColor: '#f5f5f5' }}
                                      placeHolder="The email address your customers will see"
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary me-2"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSendTestEmail(values)}
                                disabled={savingSection === 'test-email'}
                              >
                                {savingSection === 'test-email' ? <Loader /> : "Send Test Email"}
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={() => handleSaveSMTPConfig(values, setTouched)}
                                disabled={savingSection === 'smtp'}
                              >
                                {savingSection === 'smtp' ? <Loader /> : "Save"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                     {/* Stripe Account Connect */}
                    <div className="col-12 mb-3" style={{ padding: 0 }}>
                      <div className="accordion-item" style={{ border: '1px solid #ddd', borderRadius: '8px', marginBottom: '10px', width: '100%' }}>
                        <div
                          className="accordion-header d-flex justify-content-between align-items-center p-3"
                          style={{ cursor: 'pointer', backgroundColor: '#f8f9fa', borderRadius: '8px 8px 0 0' }}
                          onClick={() => toggleAccordion('stripe')}
                        >
                          <h5 style={{ margin: 0, fontSize: '16px' }}>Stripe Connect</h5>
                          <span style={{
                            fontSize: '12px',
                            color: '#666',
                            transition: 'transform 0.2s ease',
                            transform: openAccordion === 'address' ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'inline-block'
                          }}>▼</span>
                        </div>
                        {openAccordion === 'stripe' && (
                          <div className="accordion-body p-3">
                            <div className="row">
                              <div className="col-sm-6 col-md-6 mb-4">
                                <div className="form-group">
                                  <label className="fw-bold">Stripe Account ID</label>
                                  <div className="input-container">
                                    <input
                                      type="text"
                                      className="form-control pl-5"
                                      name="stripe_account_id"
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      value={values.stripe_account_id}
                                      readOnly
                                      style={{ backgroundColor: '#f5f5f5' }}
                                    />
                                    <img
                                      src="assets/images/event-name.png"
                                      className="input-img"
                                      alt=""
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 text-end mb-3">
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                                onClick={handleConnect} 
                                disabled={savingSection === 'stripe'}
                              >
                                {savingSection === 'stripe' ? <Loader /> : "Connect"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>


                    <div className="col-sm-6 col-md-6 text-center" style={{ display: 'none' }}>
                      <button
                        type="submit"
                        className="btn btn-save"
                        disabled={submitting}
                      >
                        {submitting ? <Loader /> : "Done"}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </article>
  );
}

export default EditCompany;
